import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { HiOutlineFilter, HiOutlineX, HiOutlineChevronDown } from "react-icons/hi";
import { useDebounce } from "../hooks/useDebounce";

// Shared marker class for portaled dropdown menus (see SelectFilter below) so
// outside-click handlers — both this component's own and the parent
// FilterDropdown's — can tell "click landed in a portaled menu" apart from
// "click landed outside everything", even though the menu is no longer a
// DOM descendant of the input it belongs to.
const PORTAL_MENU_CLASS = "filter-dropdown-portal-menu";
// Marker for the FilterDropdown panel's own portal (distinct from the
// per-select menu portal above) so outside-click handling can recognize it.
const PORTAL_PANEL_CLASS = "filter-dropdown-portal-panel";

/**
 * Single-select filter that looks and behaves like SearchableDropdown.
 * Static options — no async fetch.
 *
 * The option list is rendered via a portal into document.body and positioned
 * from the input's live getBoundingClientRect(), rather than as a
 * position:absolute child of the input. The Filters panel body scrolls
 * (overflow-y-auto) to cap the panel's own height, and an absolutely
 * positioned dropdown nested inside that scrolling box gets its available
 * space computed from the box's own (small, content-driven) height — not the
 * panel's max-height — so it would either get clipped to a sliver or, in
 * some layouts, balloon the whole panel past its footer over the page below.
 * Portaling escapes that scroll container entirely so the menu always has
 * its own correctly-computed space, flips upward when there isn't enough
 * room below, and is clamped to fit the viewport on any screen size.
 */
function SelectFilter({ filter, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuPos, setMenuPos] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);

  const selectedLabel = value
    ? (filter.options.find((o) => o.value === value)?.label ?? "")
    : "";

  const filtered = search
    ? filter.options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
      )
    : filter.options;

  // "All" (clears the filter) is always the first keyboard-navigable row,
  // same as the real options below it — matches SearchableDropdown's model
  // of one flat, arrow-key-navigable list.
  const navigableOptions = [{ value: "", label: "All" }, ...filtered];

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  useEffect(() => {
    function isOutside(target) {
      if (ref.current && ref.current.contains(target)) return false;
      if (target.closest && target.closest(`.${PORTAL_MENU_CLASS}`)) return false;
      return true;
    }
    function handleMouseDown(e) {
      if (isOutside(e.target)) close();
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [close]);

  // Reposition on open/resize. Scrolling the underlying page must NOT close
  // this menu — it should stay open (at its last computed position) until
  // the user explicitly clicks outside, so they can scroll down and back up
  // without losing the selection they were making.
  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const margin = 8;
      const preferredHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const openUp = spaceBelow < 120 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(
        80,
        Math.min(preferredHeight, openUp ? spaceAbove : spaceBelow)
      );
      setMenuPos({
        left: rect.left,
        width: rect.width,
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        maxHeight,
      });
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [isOpen, close]);

  // Re-anchor the keyboard cursor to the current value (or "All") whenever
  // the menu opens or typing narrows the visible list — mirrors
  // SearchableDropdown's behavior of always starting keyboard nav from a
  // sensible position instead of losing it on every keystroke.
  useEffect(() => {
    if (!isOpen) return;
    const idx = navigableOptions.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, search]);

  useEffect(() => {
    if (activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const open = () => {
    setIsOpen(true);
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const select = (val) => {
    onChange(val);
    close();
  };

  const handleFocus = () => {
    if (!isOpen) open();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        open();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % navigableOptions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + navigableOptions.length) % navigableOptions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < navigableOptions.length) {
        select(navigableOptions[activeIndex].value);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative" onClick={!isOpen ? open : undefined}>
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          spellCheck={false}
          readOnly={!isOpen}
          value={isOpen ? search : selectedLabel}
          onChange={(e) => isOpen && setSearch(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={isOpen ? "Type to search…" : "All"}
          className={`w-full h-[34px] pl-2.5 pr-7 text-sm border rounded-md bg-white text-[#24292f] transition-all focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 ${
            isOpen
              ? "border-[#0078d4] cursor-text"
              : "border-[#d0d5dd] cursor-pointer"
          }`}
        />
        {value ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8b949e] hover:text-[#57606a] bg-transparent border-none cursor-pointer p-0"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              select("");
            }}
          >
            <HiOutlineX className="text-xs" />
          </button>
        ) : (
          <HiOutlineChevronDown
            className={`absolute right-2 top-1/2 -translate-y-1/2 text-[#8b949e] pointer-events-none text-xs transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </div>

      {isOpen && menuPos && createPortal(
        <div
          ref={menuRef}
          className={`${PORTAL_MENU_CLASS} fixed bg-white border border-[#d0d5dd] rounded-md shadow-lg z-[400] overflow-y-auto`}
          style={{
            left: menuPos.left,
            width: menuPos.width,
            top: menuPos.top,
            bottom: menuPos.bottom,
            maxHeight: menuPos.maxHeight,
          }}
        >
          <div
            ref={(el) => { optionRefs.current[0] = el; }}
            className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
              activeIndex === 0 ? "bg-[#f3f4f6]" : ""
            } ${
              !value
                ? "bg-[#eff6ff] text-[#0078d4] font-medium"
                : "text-[#24292f]"
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => select("")}
            onMouseEnter={() => setActiveIndex(0)}
          >
            All
          </div>
          {filtered.map((opt, i) => (
            <div
              key={opt.value}
              ref={(el) => { optionRefs.current[i + 1] = el; }}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                i + 1 === activeIndex ? "bg-[#f3f4f6]" : ""
              } ${
                value === opt.value
                  ? "bg-[#eff6ff] text-[#0078d4] font-medium"
                  : "text-[#24292f]"
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(opt.value)}
              onMouseEnter={() => setActiveIndex(i + 1)}
            >
              {opt.label}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-[#8b949e] italic">
              No results
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Server-paginated variant of SelectFilter, for a filter whose full option
 * list is too large to load eagerly (e.g. thousands of schools nationwide —
 * see Users.js). Instead of a static `filter.options` array, `filter.fetchFn`
 * is `(search, page) => Promise<{ data: [{value, label}], total_count }>`.
 * Loads page 1 on open/search, then auto-loads the next page when the menu
 * is scrolled near its bottom (no separate "N more…" click needed).
 *
 * `filter.scopeKey`, if provided, is a plain value (string/number) that
 * identifies the current scope (e.g. which Block/District/State is picked
 * elsewhere in the panel) — changing it resets pagination and refetches from
 * page 1, the same way a changed search term does. `fetchFn` itself is a new
 * closure on every render (it closes over sibling filter values), so its own
 * identity can't be used as an effect dependency without refetching on every
 * keystroke anywhere in the panel.
 */
function AsyncSelectFilter({ filter, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuPos, setMenuPos] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const debouncedSearch = useDebounce(search, 400);
  // Synchronous guards for the scroll handler below -- React state (loading/
  // loadingMore) only takes effect on the next render, and a fast scroll
  // gesture fires many 'scroll' events before that render lands, so a
  // state-only guard lets several of them slip through and cascade-load
  // every page in a row. loadingMoreRef blocks overlap immediately; armedRef
  // additionally requires the user to scroll back OUT of the near-bottom
  // zone before another auto-load can fire, rather than re-firing on every
  // scroll event while still sitting inside that zone.
  const loadingMoreRef = useRef(false);
  const armedRef = useRef(true);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  useEffect(() => {
    function isOutside(target) {
      if (ref.current && ref.current.contains(target)) return false;
      if (target.closest && target.closest(`.${PORTAL_MENU_CLASS}`)) return false;
      return true;
    }
    function handleMouseDown(e) {
      if (isOutside(e.target)) close();
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [close]);

  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const margin = 8;
      const preferredHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const openUp = spaceBelow < 120 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(
        80,
        Math.min(preferredHeight, openUp ? spaceAbove : spaceBelow)
      );
      setMenuPos({
        left: rect.left,
        width: rect.width,
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        maxHeight,
      });
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [isOpen]);

  // (Re)load page 1 whenever the menu opens, the search term settles, or the
  // scope changes — a stale scope's results would otherwise linger onscreen
  // under a now-different Block/District/State selection.
  useEffect(() => {
    if (!isOpen || !filter.fetchFn) return;
    let cancelled = false;
    armedRef.current = true;
    loadingMoreRef.current = false;
    setLoading(true);
    filter.fetchFn(debouncedSearch, 1)
      .then((res) => {
        if (cancelled) return;
        const items = res.data || [];
        setOptions(items);
        setPage(1);
        // A short page (fewer rows than asked for) is the reliable end-of-list
        // signal. Comparing accumulated-unique-count against the server's
        // total_count instead can get stuck forever: if any two rows tie on
        // the sort column (e.g. two differently-scoped districts/blocks with
        // the same name -- common in India), an unstable sort can return the
        // same row on two different pages or skip one, and the deduped count
        // then never quite reaches total_count.
        setHasMore(items.length >= (filter.pageSize || 20));
      })
      .catch(() => {
        if (!cancelled) { setOptions([]); setHasMore(false); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearch, filter.scopeKey]);

  // Resolve the selected value's label from whatever page it was picked
  // from — same known limitation as SearchableDropdown without a resolveFn:
  // a value selected in a prior session/scope may show blank until searched.
  useEffect(() => {
    if (!value) { setSelectedLabel(""); return; }
    const found = options.find((o) => o.value === value);
    setSelectedLabel(found ? found.label : "");
  }, [value, options]);

  // Blank the shown label immediately when the scope changes (e.g. a
  // different State picked above this filter), instead of leaving a stale
  // name sitting in a CLOSED box until it happens to be reopened -- `options`
  // above only refreshes on open/search, so without this a closed dropdown
  // keeps showing a previous scope's label (e.g. "Aligarh") even after
  // picking a State it doesn't belong to, until the user clicks it.
  useEffect(() => {
    setSelectedLabel("");
  }, [filter.scopeKey]);

  const open = () => {
    setIsOpen(true);
    setSearch("");
  };

  const select = (val) => {
    onChange(val);
    close();
  };

  const handleFocus = () => {
    if (!isOpen) open();
  };

  const handleMenuScroll = () => {
    if (!menuRef.current || !filter.fetchFn) return;
    const { scrollTop, scrollHeight, clientHeight } = menuRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight <= 48;
    if (!nearBottom) {
      armedRef.current = true; // left the trigger zone -- re-arm for the next approach
      return;
    }
    if (!armedRef.current || loadingMoreRef.current || loading || !hasMore) return;
    armedRef.current = false; // one load per approach to the bottom, not one per scroll event
    loadingMoreRef.current = true;
    const nextPage = page + 1;
    setLoadingMore(true);
    filter.fetchFn(debouncedSearch, nextPage)
      .then((res) => {
        const items = res.data || [];
        setOptions((prev) => {
          const seen = new Set(prev.map((o) => o.value));
          return [...prev, ...items.filter((o) => !seen.has(o.value))];
        });
        setPage(nextPage);
        // See the page-1 load above for why this is length-based, not a
        // comparison against total_count.
        setHasMore(items.length >= (filter.pageSize || 20));
      })
      .catch(() => {})
      .finally(() => { loadingMoreRef.current = false; setLoadingMore(false); });
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative" onClick={!isOpen ? open : undefined}>
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          spellCheck={false}
          readOnly={!isOpen}
          value={isOpen ? search : selectedLabel}
          onChange={(e) => isOpen && setSearch(e.target.value)}
          onFocus={handleFocus}
          placeholder={isOpen ? "Type to search…" : "All"}
          className={`w-full h-[34px] pl-2.5 pr-7 text-sm border rounded-md bg-white text-[#24292f] transition-all focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 ${
            isOpen ? "border-[#0078d4] cursor-text" : "border-[#d0d5dd] cursor-pointer"
          }`}
        />
        {value ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8b949e] hover:text-[#57606a] bg-transparent border-none cursor-pointer p-0"
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); select(""); }}
          >
            <HiOutlineX className="text-xs" />
          </button>
        ) : (
          <HiOutlineChevronDown
            className={`absolute right-2 top-1/2 -translate-y-1/2 text-[#8b949e] pointer-events-none text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        )}
      </div>

      {isOpen && menuPos && createPortal(
        <div
          ref={menuRef}
          onScroll={handleMenuScroll}
          className={`${PORTAL_MENU_CLASS} fixed bg-white border border-[#d0d5dd] rounded-md shadow-lg z-[400] overflow-y-auto`}
          style={{ left: menuPos.left, width: menuPos.width, top: menuPos.top, bottom: menuPos.bottom, maxHeight: menuPos.maxHeight }}
        >
          <div
            className={`px-3 py-2 text-sm cursor-pointer transition-colors ${!value ? "bg-[#eff6ff] text-[#0078d4] font-medium" : "text-[#24292f]"}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => select("")}
          >
            All
          </div>
          {loading ? (
            <div className="px-3 py-2 text-sm text-[#8b949e]">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#8b949e] italic">No results</div>
          ) : (
            options.map((opt) => (
              <div
                key={opt.value}
                className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                  value === opt.value ? "bg-[#eff6ff] text-[#0078d4] font-medium" : "text-[#24292f]"
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(opt.value)}
              >
                {opt.label}
              </div>
            ))
          )}
          {loadingMore && (
            <div className="px-3 py-2 text-xs text-[#8b949e] text-center">Loading more…</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Reusable filter dropdown component.
 *
 * Props:
 *   filters: Array of filter config objects:
 *     { key, label, type, options }
 *       - type: "select" | "multi-select"
 *       - options: [{ value, label }]
 *   values: Object keyed by filter key with current values.
 *   onChange: (newValues) => void  -- called on Apply
 */
// `onOpen` (optional) fires each time the panel opens — lets a page defer
// fetching a select filter's options until the panel is actually used.
export default function FilterDropdown({ filters, values, onChange, onDraftChange, onOpen }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(values);
  const [multiSearch, setMultiSearch] = useState({});
  const [panelPos, setPanelPos] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    setDraft(values);
  }, [values]);

  useEffect(() => {
    function handleClick(e) {
      // A SelectFilter's option list is portaled to document.body (see
      // SelectFilter above), so a click on one of its options is not a DOM
      // descendant of `ref` — without this check it would look like an
      // outside click and close the whole panel before the selection lands.
      if (e.target.closest && e.target.closest(`.${PORTAL_MENU_CLASS}`)) return;
      // The panel itself is now also portaled to document.body (see below),
      // so a click anywhere inside it is likewise not a descendant of `ref`.
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setDraft(values);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, values]);

  useEffect(() => {
    if (!open) setMultiSearch({});
  }, [open]);

  // The panel is portaled to document.body and positioned from the button's
  // live getBoundingClientRect(), rather than as a position:absolute child
  // of the button. A list card that renders very short (e.g. a "No results
  // found" empty state) has little height of its own, and since the button
  // lives inside that card, an absolutely-positioned panel would be clipped
  // wherever an ancestor card sets overflow-hidden with a shrunk height —
  // portaling escapes that entirely so the panel always has room to render.
  // Reposition on open/resize only. Scrolling the underlying page must NOT
  // close this panel — it stays open at its last computed position until
  // the user clicks outside, so scrolling the list behind it doesn't force
  // them to scroll back up just to find their filter selections again.
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const panelWidth = 320; // w-80
      const margin = 8;
      let left = rect.right - panelWidth;
      left = Math.max(margin, Math.min(left, window.innerWidth - panelWidth - margin));
      setPanelPos({ top: rect.bottom + margin, left });
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeCount = filters.reduce((count, f) => {
    const v = values[f.key];
    if (f.type === "multi-select")
      return count + (Array.isArray(v) && v.length > 0 ? 1 : 0);
    return count + (v !== "" && v !== undefined && v !== null ? 1 : 0);
  }, 0);

  const handleSelectChange = (key, val) => {
    const next = { ...draft, [key]: val };
    setDraft(next);
    if (onDraftChange) onDraftChange(next);
  };

  const handleMultiChange = (key, val) => {
    setDraft((prev) => {
      const arr = prev[key] || [];
      const next = {
        ...prev,
        [key]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val],
      };
      if (onDraftChange) onDraftChange(next);
      return next;
    });
  };

  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleClear = () => {
    const cleared = {};
    filters.forEach((f) => {
      cleared[f.key] = f.type === "multi-select" ? [] : "";
    });
    setDraft(cleared);
    onChange(cleared);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className={`inline-flex items-center gap-2 h-10 px-4 text-sm font-medium rounded-md cursor-pointer transition-all duration-200 border ${
          activeCount > 0
            ? "bg-[#eff6ff] text-[#0078d4] border-[#0078d4]"
            : "bg-white text-[#24292f] border-[#d0d5dd] hover:bg-[#f3f4f6] hover:border-[#8b949e]"
        }`}
        onClick={() => { if (!open && onOpen) onOpen(); setOpen(!open); }}
        type="button"
      >
        <HiOutlineFilter className="text-base" />
        Filters
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#0078d4] text-white text-[10px] font-bold">
            {activeCount}
          </span>
        )}
      </button>

      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          className={`${PORTAL_PANEL_CLASS} fixed w-80 bg-white border border-[#e5e7eb] rounded-lg shadow-lg z-50 max-h-[calc(100vh-200px)] flex flex-col`}
          style={{ top: panelPos.top, left: panelPos.left }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb] flex-shrink-0">
            <span className="text-sm font-semibold text-[#24292f]">Filters</span>
            <button
              className="p-1 rounded hover:bg-[#f3f4f6] border-none bg-transparent cursor-pointer text-[#8b949e] hover:text-[#57606a]"
              onClick={() => { setOpen(false); setDraft(values); }}
              type="button"
            >
              <HiOutlineX className="text-base" />
            </button>
          </div>

          {/* Filter sections */}
          <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-300px)]">
            {filters.map((f) => {
              const ms = (multiSearch[f.key] || "").toLowerCase();
              const filteredOpts = ms
                ? f.options.filter((o) => o.label.toLowerCase().includes(ms))
                : f.options;

              return (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-[#57606a] mb-2.5">
                    {f.label}
                  </label>

                  {f.type === "select" && (
                    <SelectFilter
                      filter={f}
                      value={draft[f.key] ?? ""}
                      onChange={(val) => handleSelectChange(f.key, val)}
                    />
                  )}

                  {f.type === "async-select" && (
                    <AsyncSelectFilter
                      filter={f}
                      value={draft[f.key] ?? ""}
                      onChange={(val) => handleSelectChange(f.key, val)}
                    />
                  )}

                  {f.type === "multi-select" && (
                    <div>
                      {f.options.length > 4 && (
                        <div className="relative mb-2.5">
                          <input
                            type="text"
                            placeholder={`Search ${f.label.toLowerCase()}…`}
                            value={multiSearch[f.key] || ""}
                            onChange={(e) =>
                              setMultiSearch((p) => ({ ...p, [f.key]: e.target.value }))
                            }
                            className="w-full h-[34px] pl-2.5 pr-7 text-sm border border-[#d0d5dd] rounded-md bg-white text-[#24292f] focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20"
                          />
                          {multiSearch[f.key] && (
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8b949e] hover:text-[#57606a] bg-transparent border-none cursor-pointer p-0"
                              onClick={() =>
                                setMultiSearch((p) => ({ ...p, [f.key]: "" }))
                              }
                            >
                              <HiOutlineX className="text-xs" />
                            </button>
                          )}
                        </div>
                      )}
                      <div className="space-y-1 max-h-36 overflow-y-auto">
                        {filteredOpts.map((opt) => (
                          <label
                            key={opt.value}
                            className="flex items-center gap-2 py-1 px-2 rounded hover:bg-[#f6f8fa] cursor-pointer text-sm text-[#24292f]"
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-[#0078d4] cursor-pointer"
                              checked={(draft[f.key] || []).includes(opt.value)}
                              onChange={() => handleMultiChange(f.key, opt.value)}
                            />
                            {opt.label}
                          </label>
                        ))}
                        {filteredOpts.length === 0 && (
                          <div className="px-2 py-2 text-xs text-[#8b949e] italic">
                            No results
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#e5e7eb] flex-shrink-0">
            <button
              className="text-sm font-medium text-[#57606a] hover:text-[#24292f] bg-transparent border-none cursor-pointer px-2 py-1 rounded hover:bg-[#f3f4f6]"
              onClick={handleClear}
              type="button"
            >
              Clear all
            </button>
            <button
              className="inline-flex items-center h-8 px-4 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
              onClick={handleApply}
              type="button"
            >
              Apply
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
